import React, { createContext, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import type { DynamicContentSlotEntryType } from 'tsconfig/types';

// Context for loading state
export const CmsContentSlotLoadingContext = createContext<
  [boolean, React.Dispatch<React.SetStateAction<boolean>>] | undefined
>(undefined);

export interface CmsContentSlotProps {
  contentItem?: Pick<DynamicContentSlotEntryType, 'fields'>;
  children?: React.ReactNode;
}

const CmsDynamicContentSlot: React.FC<CmsContentSlotProps> = ({ contentItem, children }) => {
  const placeholder = contentItem?.fields?.placeholder;
  const [loading, setLoading] = useState(!!placeholder);
  const theme = useTheme();
  // Always call hooks before any return
  const loadingContextValue: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = React.useMemo(
    () => [loading, setLoading],
    [loading],
  );

  if (!contentItem) {
    return null;
  }

  const { mobilePixelHeight, desktopPixelHeight, targetId, entryTitle } = contentItem.fields;
  const safeMobilePixelHeight = typeof mobilePixelHeight === 'number' ? mobilePixelHeight : 0;
  const safeDesktopPixelHeight = typeof desktopPixelHeight === 'number' ? desktopPixelHeight : 0;
  const placeHolderUI = placeholder?.toLowerCase();

  const container = css`
    height: ${safeMobilePixelHeight}px;
    overflow: hidden;
    ${placeHolderUI === 'image' ? 'position: relative;' : ''}
    .banner .banner-image-wrapper {
      min-height: unset;
    }
    ${theme.breakpoints.md} {
      height: ${safeDesktopPixelHeight}px;
      .banner-image-wrapper {
        height: ${safeDesktopPixelHeight}px;
      }
    }
    ${theme.widgets.CmsContentSlot?.skeleton};
  `;

  const cmsId = entryTitle ? `dynamic-content-slot-${entryTitle}` : 'dynamic-content-slot';

  return (
    <CmsContentSlotLoadingContext.Provider value={loadingContextValue}>
      <div
        data-cms-id={cmsId}
        css={container}
        data-target-id={targetId}
        className={loading ? placeHolderUI : ''}
        aria-busy={loading}
      >
        {children}
      </div>
    </CmsContentSlotLoadingContext.Provider>
  );
};
export default CmsDynamicContentSlot;
